import { Checkbox, Stack, Text, TextInput } from '@mantine/core'
import { QrPositionPicker } from './QrPositionPicker'

export interface QrConfig {
  qrEnabled: boolean
  destinationUrl: string
  qrPositionXPercent: number | null
  qrPositionYPercent: number | null
  qrSizePercent: number | null
}

interface QrSetupStepProps {
  config: QrConfig
  onChange: (config: QrConfig) => void
  baseImageUrl: string | null
  showToggle?: boolean
  onInteracted?: () => void
}

const MIN_QR_SIZE_PERCENT = 8

export const QrSetupStep = ({
  config,
  onChange,
  baseImageUrl,
  showToggle = true,
  onInteracted,
}: QrSetupStepProps) => {
  const update = (partial: Partial<QrConfig>) =>
    onChange({ ...config, ...partial })

  const isValidUrl = (() => {
    if (!config.destinationUrl) return true
    try {
      const u = new URL(config.destinationUrl)
      return u.protocol === 'http:' || u.protocol === 'https:'
    } catch {
      return false
    }
  })()

  return (
    <Stack>
      {showToggle && (
        <Checkbox
          label='Add a QR code to this ad so viewers can scan to visit your website'
          checked={config.qrEnabled}
          onChange={(e) => update({ qrEnabled: e.currentTarget.checked })}
        />
      )}
      {(config.qrEnabled || !showToggle) && (
        <>
          <TextInput
            label='Destination URL'
            description='Where scanners will be taken. Cannot be changed after creation.'
            placeholder='https://your-website.com/landing'
            value={config.destinationUrl}
            onChange={(e) => update({ destinationUrl: e.currentTarget.value })}
            error={
              config.destinationUrl && !isValidUrl
                ? 'Enter a valid http(s) URL'
                : null
            }
            radius='md'
          />
          <Text size='sm' c='dimmed'>
            Drag and resize the QR placeholder to position it on your ad. Keep
            it at least {MIN_QR_SIZE_PERCENT}% of the image width so it scans
            reliably from across a room.
          </Text>
          <QrPositionPicker
            baseImageUrl={baseImageUrl}
            xPercent={config.qrPositionXPercent}
            yPercent={config.qrPositionYPercent}
            sizePercent={config.qrSizePercent}
            minSizePercent={MIN_QR_SIZE_PERCENT}
            onChange={(x, y, size) =>
              update({
                qrPositionXPercent: x,
                qrPositionYPercent: y,
                qrSizePercent: size,
              })
            }
            onInteracted={onInteracted}
          />
          <Text size='xs' c='dimmed'>
            We record anonymous scan counts per location. We do not store
            personal data.
          </Text>
        </>
      )}
    </Stack>
  )
}
